import { useEffect, useRef, useState } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import ChevronDownIcon from "../../../../assets/ChevronDownIcon";

interface FilterOption {
    value: string;
    label: string;
}

interface FilterDropdownProps {
    label: string;
    options: FilterOption[];
    selectedValues: string[];
    onToggle: (value: string) => void;
    onClearAll: () => void;
    className?: string;
    dropdownWidth?: string;
    onOpen?: () => void;
    isOpen: boolean;
    onToggleOpen: () => void;
}

const FilterDropdown = ({
    label,
    options,
    selectedValues,
    onToggle,
    onClearAll,
    dropdownWidth = "w-52",
    isOpen,
    onToggleOpen,
}: FilterDropdownProps) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node) && isOpen) {
                onToggleOpen();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onToggleOpen]);

    const isAllSelected = selectedValues.length === 0;

    const getButtonLabel = () => {
        if (selectedValues.length === 0) return label;
        if (selectedValues.length === 1) {
            const found = options.find((o) => o.value === selectedValues[0]);
            return found?.label || label;
        }
        return `${selectedValues.length} selected`;
    };

    const handleClick = (value: string) => {
        if (value === "ALL") {
            onClearAll();
        } else {
            onToggle(value);
        }
    };

    const isSelected = (value: string) =>
        value === "ALL" ? isAllSelected : selectedValues.includes(value);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={onToggleOpen}
                className="flex items-center gap-1 px-3 py-2 border border-brColor rounded-lg text-sm font-semibold text-textColor3 shadow-light-sm hover:bg-gray-50"
            >
                {getButtonLabel()}
                <ChevronDownIcon />
            </button>
            {isOpen && (
                <div className={`absolute right-0 mt-1 ${dropdownWidth} bg-white border border-[#e5e5e5] rounded-lg shadow-[0px_12px_20px_0px_rgba(0,0,0,0.1)] z-20 p-2 flex flex-col gap-2 max-h-60 overflow-y-auto`}>
                    {options.map((opt) => {
                        const selected = isSelected(opt.value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleClick(opt.value)}
                                className={`flex items-center gap-3 w-full px-3 py-3 rounded text-base leading-6 cursor-pointer transition-colors ${
                                    selected
                                        ? "bg-[#fafafa] text-[#101828]"
                                        : "text-[#475467] hover:bg-gray-50"
                                }`}
                            >
                                <span className="flex-1 text-left">{opt.label}</span>
                                {selected && <CheckIcon className="w-5 h-5 shrink-0 text-[#101828]" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;
